/**
 * The second step of a CSV import — decision 56.
 *
 * Picking a file reads it and shows this; nothing is written until the button
 * here is pressed. That is the whole point of the two-step: the destructive
 * part of "replace the register" is invisible in a file picker, and a person
 * choosing `aksjeeierbok-2026-08-14.csv` from a folder of similar names has no
 * way to see they picked last week's copy until the register is already gone.
 *
 * So this states the counts, **names the shareholders that will be removed**,
 * says which of them take files with them, and says there is no undo. Four
 * statements, each of which is the one somebody will wish they had read.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import type { ImportPlan } from './csv'
import './ImportCsvModal.css'

export function ImportCsvModal({
  plan,
  onClose,
  onApply,
}: {
  plan: ImportPlan
  onClose: () => void
  onApply: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apply = async () => {
    setBusy(true)
    setError(null)
    try {
      await onApply()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The import failed.')
      setBusy(false)
    }
  }

  const dropped = plan.dropped.length

  return (
    <Modal
      title="Replace the share register"
      onClose={onClose}
      width={620}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {!plan.error && (
            <button className="btn btn--danger" onClick={() => void apply()} disabled={busy}>
              {busy ? 'Replacing…' : `Replace ${plan.currentCount} with ${plan.rowCount}`}
            </button>
          )}
        </>
      }
    >
      <div className="csvimp">
        <p className="csvimp__file">{plan.fileName}</p>

        {plan.error && <p className="csvimp__refused">{plan.error}</p>}

        {!plan.error && (
          <>
            <div className="csvimp__warn">
              <p className="csvimp__warnhead">This replaces the whole register, it does not merge.</p>
              <p className="csvimp__warnbody">
                {plan.rowCount} rows in the file will replace the {plan.currentCount} shareholders on
                the register. Every shareholder, holding, option grant and link is taken from the
                file — anything edited in the tool since the export is overwritten. There is no undo.
              </p>
            </div>

            <div className="csvimp__notes">
              {dropped > 0 && (
                <p className="csvimp__note csvimp__note--drop">
                  {dropped} shareholder{dropped === 1 ? '' : 's'} on the register{' '}
                  {dropped === 1 ? 'is' : 'are'} not in the file and will be removed:{' '}
                  {plan.dropped.join(', ')}.
                </p>
              )}

              {plan.droppedWithFiles.length > 0 && (
                <p className="csvimp__note">
                  Attachments are matched back by id and survive the import. These records are being
                  removed, so their attachments go with them: {plan.droppedWithFiles.join(', ')}.
                </p>
              )}

              {plan.missing.length > 0 && (
                <p className="csvimp__note">
                  Columns not in the file, so they will be cleared: {plan.missing.join(', ')}.
                </p>
              )}
            </div>
          </>
        )}

        {error && <p className="csvimp__refused">{error}</p>}
      </div>
    </Modal>
  )
}
